/**
 * LoadingPhrase — Claude-Code-style rotating loading line.
 *
 * Cycles through a finance-flavored phrase pool every 3s with a 200ms
 * crossfade and a small spinning indicator. The pool is keyed by `kind` so
 * we never lie about what's running:
 *   - chat       → fully generic ("Thinking it through…")
 *   - extraction → only used when a term sheet is being parsed
 *   - scoring    → only used when scoring/benchmarking is running
 *
 * Rotation: random shuffle, no back-to-back repeats. First phrase shows
 * immediately on mount (no 3s wait). Component is purely visual — when the
 * underlying work finishes, the parent unmounts us and the cycle stops.
 */
import { useEffect, useRef, useState } from 'react';

const PHRASES = {
  chat: [
    'Thinking it through…',
    'Working through it…',
    'Crunching the numbers…',
    'Running the analysis…',
    'Putting the pieces together…',
    'Connecting the dots…',
    'Reading between the lines…',
    'Weighing the trade-offs…',
    'Doing the math…',
    'Reasoning it through…',
    'Checking the work…',
    'Sifting through the details…',
    'Squaring the numbers…',
    'Sorting signal from noise…',
    'Threading the needle…',
    'Doing the diligence…',
  ],
  extraction: [
    'Reading the fine print…',
    'Tallying the fees…',
    'Auditing the covenants…',
    'Decoding the rate clauses…',
    'Pricing the optionality…',
    'Sniffing out hidden fees…',
    'Footing the schedule…',
    'Cross-checking the term sheet…',
  ],
  scoring: [
    'Benchmarking against peers…',
    'Stress-testing the covenants…',
    'Marking to market…',
    'Triangulating the all-in cost…',
    'Weighing the collateral…',
    'Pricing the optionality…',
  ],
} as const;

export type LoadingPhraseKind = keyof typeof PHRASES;

const ROTATION_MS = 2800; // visible duration per phrase (200ms fade brings cycle to 3s)
const FADE_MS = 200;

function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export interface LoadingPhraseProps {
  kind: LoadingPhraseKind;
  /** Override the wrapper className (size, color, padding). */
  className?: string;
}

export function LoadingPhrase({
  kind,
  className = 'text-caption text-txt-tertiary',
}: LoadingPhraseProps) {
  const pool = PHRASES[kind];
  const [phrase, setPhrase] = useState<string>(() => shuffle(pool)[0]);
  const [visible, setVisible] = useState(true);
  const queueRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    // Seed queue with a fresh shuffle, skipping the phrase already on screen
    // so we don't get an instant repeat.
    const initialShuffle = shuffle(pool);
    queueRef.current = initialShuffle.filter((p) => p !== phrase);

    const tick = () => {
      timer = setTimeout(() => {
        if (cancelled) {return;}
        setVisible(false);
        timer = setTimeout(() => {
          if (cancelled) {return;}
          if (queueRef.current.length === 0) {
            // Refill, avoiding back-to-back repeat across cycles.
            const next = shuffle(pool);
            if (next[0] === phrase && next.length > 1) {
              [next[0], next[1]] = [next[1], next[0]];
            }
            queueRef.current = next;
          }
          const nextPhrase = queueRef.current.shift()!;
          setPhrase(nextPhrase);
          setVisible(true);
          tick();
        }, FADE_MS);
      }, ROTATION_MS);
    };
    tick();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // We intentionally only depend on `pool`. Re-running on every `phrase`
    // change would reset the cycle on each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);

  return (
    <div
      className={`flex items-center gap-1.5 ${className}`}
      role="status"
      aria-live="polite"
    >
      <span
        className="inline-block w-2 h-2 bg-orange animate-credia-pulse flex-shrink-0"
        aria-hidden
      />
      <span
        className="transition-opacity"
        style={{
          transitionDuration: `${FADE_MS}ms`,
          opacity: visible ? 1 : 0,
        }}
      >
        {phrase}
      </span>
    </div>
  );
}
